import React, { useState, useEffect, useMemo } from 'react';

import { SearchOutlined, SwapOutlined, LinkOutlined } from '@ant-design/icons';

import MerchantSelect from '@components/operator/MerchantSelect';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import PageHeader from '@shared/PageHeader';
import { Card, Table, Tag, message, Input, Button, Modal, Checkbox, Radio, Space } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { operatorApiFetch } from '@services/operator/apiService';

function CollectList() {
  const { t } = useTranslation();
  const { merchantId, merchant } = useOperatorData();
  const [loading, setLoading] = useState(false);
  const [returnOrders, setReturnOrders] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [skipVerification, setSkipVerification] = useState(false);
  const [inboundType, setInboundType] = useState('quantity'); // 'quantity' or 'box'
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (merchantId) {
      fetchReturnOrders();
    }
  }, [merchantId]);

  const fetchReturnOrders = async () => {
    setLoading(true);
    try {
      // 使用 URLSearchParams 構建 query string
      const params = new URLSearchParams({
        merchant_id: merchantId,
        status: 'verified',
        limit: 200
      });

      const response = await operatorApiFetch(`return_orders?${params.toString()}`, {
        method: 'GET'
      });
      const responseData = await response.json();

      console.log('Fetch return orders response:', responseData);

      if (responseData.success && responseData.data) {
        setReturnOrders(responseData.data);
      } else {
        console.warn('No data in response or request failed:', responseData);
        setReturnOrders([]);
      }
    } catch (error) {
      message.error(t('common.error.fetch_failed'));
      console.error('Failed to fetch return orders:', error);
      setReturnOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // 靜態搜尋過濾
  const filteredReturnOrders = useMemo(() => {
    if (!searchText) return returnOrders;

    const searchLower = searchText.toLowerCase();

    return returnOrders.filter((order) => {
      // 搜尋單號
      if (order.name && order.name.toLowerCase().includes(searchLower)) {
        return true;
      }

      // 搜尋驗收人
      if (order.verifier_name && order.verifier_name.toLowerCase().includes(searchLower)) {
        return true;
      }

      // 搜尋商品（品號和品名）
      if (order.order_items && order.order_items.length > 0) {
        return order.order_items.some((item) => {
          const skuMatch = item.product_sku && item.product_sku.toLowerCase().includes(searchLower);
          const nameMatch = item.product_name && item.product_name.toLowerCase().includes(searchLower);
          return skuMatch || nameMatch;
        });
      }

      return false;
    });
  }, [returnOrders, searchText]);

  const columns = useMemo(() => [
    {
      title: t('models.return_order.name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (name, record) => {
        if (!name) return '-';
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/return_orders/${record.id}`} style={{ wordBreak: 'break-word' }}>
              {name}
            </Link>
            <a
              href={`/operator/return_orders/${record.id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        );
      },
    },
    {
      title: t('operator.pages.return_orders.collect.columns.products'),
      dataIndex: 'items',
      key: 'items',
      render: (items) => {
        if (!items || items.length === 0) return '-';
        return (
          <div>
            {items.map((item, index) => (
              <div key={item.id || index} style={{ marginBottom: 4 }}>
                <Tag>{item.product_sku}</Tag>
                <span>{item.product_name} × {item.quantity}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: t('operator.pages.return_orders.collect.columns.verifier'),
      dataIndex: 'verifier_name',
      key: 'verifier_name',
      width: 150,
      render: (verifierName) => verifierName || '-',
    },
    {
      title: t('operator.pages.return_orders.collect.columns.received_at'),
      dataIndex: 'received_at',
      key: 'received_at',
      width: 120,
      render: (verifiedAt) => {
        if (!verifiedAt) return '-';
        return dayjs(verifiedAt).format('YYYY-MM-DD');
      },
    },
  ], [t]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  // 開啟 Modal
  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  // 關閉 Modal
  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSkipVerification(false);
    setInboundType('quantity');
  };

  // 確認轉入庫單
  const handleConvertToInbound = async () => {
    setSubmitting(true);
    try {
      const payload = {
        return_order_ids: selectedRowKeys,
        skip_verification: skipVerification,
        inbound_type: inboundType
      };

      const response = await operatorApiFetch('return_orders/convert_to_inbound', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const responseData = await response.json();

      if (responseData.success) {
        const inboundName = responseData.data?.inbound_name || responseData.data?.inbound_id;
        message.success(
          inboundName
            ? `${t('operator.pages.return_orders.collect.convert_success')}: ${inboundName}`
            : t('operator.pages.return_orders.collect.convert_success')
        );
        setSelectedRowKeys([]);
        handleCloseModal();
        fetchReturnOrders(); // 重新載入資料
      } else {
        message.error(responseData.message || t('operator.pages.return_orders.collect.convert_failed'));
      }
    } catch (error) {
      console.error('Failed to convert to inbound:', error);
      message.error(t('operator.pages.return_orders.collect.convert_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Card
        title={t('operator.pages.return_orders.collect.title')}
        extra={
          <Input
            placeholder={t('operator.pages.return_orders.collect.search_placeholder')}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        }
        bordered={false}
      >
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredReturnOrders}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ y: 600 }}
        />
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Space>
              {selectedRowKeys.length > 0 ? (
                <>
                  <Tag color="blue">
                    {t('operator.pages.return_orders.collect.selected_count', { count: selectedRowKeys.length })}
                  </Tag>
                  <Button
                    type="primary"
                    icon={<SwapOutlined />}
                    onClick={handleOpenModal}
                  >
                    {t('operator.pages.return_orders.collect.convert_to_inbound')}
                  </Button>
                </>
              ) : null}
            </Space>
          </div>
          <div>
            <Tag color="default">
              {t('operator.pages.return_orders.collect.total_count', {
                total: returnOrders.length,
                filtered: filteredReturnOrders.length
              })}
            </Tag>
          </div>
        </div>
      </Card>

      {/* 轉入庫單 Modal */}
      <Modal
        title={t('operator.pages.return_orders.collect.modal.title')}
        open={isModalVisible}
        onCancel={handleCloseModal}
        onOk={handleConvertToInbound}
        confirmLoading={submitting}
        okText={t('operator.pages.return_orders.collect.modal.confirm')}
        cancelText={t('common.button.cancel')}
        width={500}
      >
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              {t('operator.pages.return_orders.collect.modal.selected_orders')}
            </div>
            <Tag color="blue">{selectedRowKeys.length} {t('operator.pages.return_orders.collect.modal.orders_unit')}</Tag>
          </div>

          <div>
            <Checkbox
              checked={skipVerification}
              onChange={(e) => setSkipVerification(e.target.checked)}
            >
              {t('operator.pages.return_orders.collect.modal.skip_verification')}
            </Checkbox>
          </div>

          <div>
            <div style={{ marginBottom: 12, fontWeight: 500 }}>
              {t('operator.pages.return_orders.collect.modal.inbound_type_label')}
            </div>
            <Radio.Group
              value={inboundType}
              onChange={(e) => setInboundType(e.target.value)}
            >
              <Space direction="vertical">
                <Radio value="quantity">
                  {t('operator.pages.return_orders.collect.modal.inbound_type_quantity')}
                </Radio>
                <Radio value="box">
                  {t('operator.pages.return_orders.collect.modal.inbound_type_box')}
                </Radio>
              </Space>
            </Radio.Group>
          </div>
        </Space>
      </Modal>
    </div>
  );
}

export default function ReturnOrderCollect() {
  const { t } = useTranslation();

  return (
    <MerchantSelect
      title={t('operator.pages.return_orders.collect.merchant_selection_title')}
      subtitle={t('operator.pages.return_orders.collect.merchant_selection_subtitle')}
    >
      <div>
        <PageHeader items={['return_orders', 'return_orders.collect']} />
        <CollectList />
      </div>
    </MerchantSelect>
  );
}
